import {
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Typography,
} from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import ButtonGroup, { ButtonGroupItem } from 'components/ButtonGroup';
import DialogAlert, {
  DialogAlertInitial,
  DialogAlertProps,
} from 'components/DialogAlert';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import i18n from 'i18n';
import React from 'react';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import SkeletonTemplate from 'containers/Groups/Template/Skeleton';
import Button from 'components/Button';
import { SearchBar } from 'components/SearchBar';
import styles from '../../Template/styles';

const useStyles = makeStyles(styles);

interface Props {
  alert?: DialogAlertProps | null;
  children: React.ReactNode;
  contentTitle?: string;
  contentSubtitle?: string;
  disableArea?: boolean;
  generalActions?: ButtonGroupItem[];
  initialQuery: {
    offset: number;
    limit: number;
    search: string;
  };
  listSwitch: boolean;
  marginSearchTop?: boolean;
  noListAll?: boolean;
  noTitleBlock?: boolean;
  searchField: string;
  selectAll?: {
    value: boolean;
    label: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  };
  slug?: 'COMPANIES' | 'PARTNERS' | 'ENVIRONMENTS' | 'CLIENTS' | 'OPERATORS';
  subtitle?: React.ReactNode;
  title?: string;
  isLoading: boolean;
  getRoles: (refresh?: boolean) => void;
  handleGetRoles: (search: string) => void;
  onRefresh?: () => void;
  onSearch?: (search?: string | undefined) => void;
  setListSwitch: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  setQuery: React.Dispatch<
    React.SetStateAction<{
      offset: number;
      limit: number;
      search: string;
    }>
  >;
}

const TemplateGroupsList = ({
  alert,
  children,
  contentTitle,
  contentSubtitle,
  disableArea,
  generalActions,
  initialQuery,
  listSwitch,
  noListAll,
  noTitleBlock,
  searchField,
  selectAll,
  slug,
  subtitle,
  title,
  isLoading,
  getRoles,
  onRefresh,
  onSearch,
  handleGetRoles,
  setListSwitch,
  setSearchField,
  setQuery,
}: Props) => {
  const classes = useStyles();
  const [alertData, setAlertData] = React.useState<DialogAlertProps>(
    DialogAlertInitial
  );

  const defaultAlertProps = {
    icon: <NotificationsActiveIcon className={classes.iconFormat} />,
    onClose: () => setAlertData(DialogAlertInitial),
  };

  React.useEffect(() => {
    const newAlertData = alert || DialogAlertInitial;

    setAlertData({
      ...defaultAlertProps,
      ...newAlertData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert]);

  return (
    <React.Fragment>
      <Grid
        container
        className={clsx({
          [classes.disableArea]: disableArea,
        })}
      >
        {!noTitleBlock && (
          <Grid
            container
            direction="row"
            alignItems="center"
            justify="space-between"
          >
            <PageTitle title={title} subtitle={subtitle} />

            <Grid
              container
              direction="row"
              alignItems="center"
              justify="space-between"
              style={{ marginTop: 24 }}
            >
              <Grid item>
                <Typography className={classes.contentTitle}>
                  {contentTitle}
                </Typography>
                <Typography className={classes.contentSubtitle}>
                  {contentSubtitle}
                </Typography>
              </Grid>
              <Grid
                container
                xs={3}
                direction="row"
                alignItems="center"
                justify="flex-end"
              >
                {onRefresh && (
                  <RefreshButton
                    disabled={isLoading}
                    id="btn-refresh"
                    onClick={onRefresh}
                    animate={isLoading}
                  />
                )}

                {generalActions && (
                  <ButtonGroup
                    style={{ marginLeft: 16 }}
                    label={i18n.t('GENERAL_ACTIONS_KEY')}
                    options={generalActions}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        )}

        {disableArea ? (
          <SkeletonTemplate />
        ) : (
          <>
            {selectAll && (
              <Grid item xs={12} className={classes.selectAll}>
                <FormControlLabel
                  control={
                    <Switch
                      id="select-all"
                      checked={selectAll.value}
                      onChange={selectAll.onChange}
                      size="small"
                    />
                  }
                  label={selectAll.label}
                />
              </Grid>
            )}
            {!selectAll?.value ? (
              <>
                {onSearch && (
                  <Grid
                    container
                    spacing={2}
                    className={classes.searchContainer}
                  >
                    <Grid item className={classes.searchSize}>
                      <SearchBar
                        id="search-groups"
                        isLoading={isLoading}
                        searchValue={searchField}
                        handleSearch={onSearch}
                        onSearchComplete={() => setListSwitch(true)}
                        setSearchValue={setSearchField}
                      />
                    </Grid>
                    <Grid item className={classes.buttonsGrid}>
                      <Button
                        className={clsx(classes.searchButtonSize, {
                          [classes.buttonIsLoading]: isLoading,
                        })}
                        variant="outlined"
                        onClick={() => {
                          handleGetRoles(searchField);
                        }}
                        disabled={isLoading}
                      >
                        <Typography className={classes.listAll}>
                          {i18n.t('SEARCH_ALL_KEY')}
                        </Typography>
                      </Button>
                      {!noListAll && (
                        <Button
                          className={clsx(classes.listButtonSize, {
                            [classes.buttonIsLoading]: isLoading,
                          })}
                          id="list-all-groups"
                          variant="outlined"
                          pressed={listSwitch}
                          disabled={isLoading}
                          onClick={() => {
                            if (listSwitch === false) {
                              getRoles();
                              setListSwitch(!listSwitch);
                              setQuery(initialQuery);
                            } else {
                              setListSwitch(!listSwitch);
                              setQuery(initialQuery);
                            }
                          }}
                        >
                          <Typography className={classes.listAll}>
                            {i18n.t('LIST_ALL_KEY')}
                          </Typography>
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                )}
                {listSwitch && (
                  <Grid container className={classes.content}>
                    {children}
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <Alert severity="info">
                  {i18n.t('INFO_AUTOMATICALLY_ADDED_KEY', {
                    more: i18n.t(`NEW_${slug}_KEY`),
                  })}
                </Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <DialogAlert {...alertData} />
    </React.Fragment>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  role: data?.role,
  companySelected,
});

export default connect(mapStateToProps)(TemplateGroupsList);
